<script>
import {mapActions, mapState, mapGetters} from 'vuex';
import DropDownMenu from '../DropdownMenu/DropdownMenu.vue';
import PaginationControl from '../PaginationControl/PaginationControl.vue';
import ManageAllocationRequestForm from '../ManageAllocationRequestForm/ManageAllocationRequestForm.vue';
import {getCalendarHighlightIndices} from "../../services/DateToolService";

export default {
    components: {
        PaginationControl,
        DropDownMenu,
        ManageAllocationRequestForm
    },
    filters: {
        blockFilter(bool) {
            return bool ? 'X' : null
        }
    },
    data() {
        return {
            open: false
        }
    },
    computed: {
        ...mapState({
            pending: state => state.allocation.request.items.pending,
            accepted: state => state.allocation.request.items.approved,
            declined: state => state.allocation.request.items.denied,
            modified: state => state.allocation.request.items.modified,
            predicate: state => state.allocation.request.predicate,
            predicates: state => state.allocation.request.predicates,
            item: state => state.allocation.request.item,
            page: state => state.allocation.request.page,
            limit: state => state.allocation.request.limit,
            contractManager: state => state.auth.user.contractManager,
            editMode: state => state.allocation.request.editMode,
            trusted: state => state.auth.supplier.trusted
        }),
        ...mapGetters({
            requestCount: 'allocation/requestCount',
            requestsPaginated: 'allocation/requestsPaginated',
            requestsMaxPages: 'allocation/requestsMaxPages'
        })
    },
    methods: {
        ...mapActions({
            requestAccept: 'allocation/requestAccept',
            requestAcceptModified: 'allocation/requestAcceptModified',
            requestDeny: 'allocation/requestDeny',
            requestEdit: 'allocation/requestEdit',
            requestCancel: 'allocation/requestCancel',
            requestPredicateSet: 'allocation/requestPredicateSet',
            requestPageIncrement: 'allocation/requestPageIncrement',
            requestPageDecrement: 'allocation/requestPageDecrement',
            requestItemSelect: 'allocation/requestItemSelect',
            requestEditToggle: 'allocation/requestEditToggle',
        }),
        enableEdit(request, index) {
            const indices = getCalendarHighlightIndices(request);
            this.requestItemSelect({request: request, index: index + ((this.page-1) * this.limit), indices: indices});
            this.requestEditToggle();
        },
        handleRequest(action, requestId) {
            switch (action) {
            case 'accept':
                this.requestAccept(requestId).then(() => {
                    this.fetchData();
                });
                break;
            case 'deny':
                this.requestDeny(requestId).then(() => {
                    this.fetchData();
                });
                break;
            case 'cancel':
                this.requestCancel(requestId).then(() => {
                    this.fetchData();
                });
                break;
            }
        },
        fetchData() {
            Promise.all([
                this.getAllocationList(),
                this.getAllocationListMerged(),
                this.getAllocationCalendar()
            ]);
        }
    }
}
</script>
<template>
    <div v-if="!trusted"
         :class="{'manage-allocation-request--open' : open}"
         class="manage-allocation-request">
        <div class="manage-allocation-request__toggle" @click="open = !open">
            <span>{{ $t('manageAllocationRequest.toggle') }} </span>
            <span class="manage-allocation-request__news">
                {{ requestCount[0] }}
            </span>
        </div>
        <manage-allocation-request-form/>
        <div class="manage-allocation-request__content">
            <div class="manage-allocation-request__header">
                <div class="manage-allocation-request__left">
                    <div class="manage-allocation-request__heading">
                        {{ $t('manageAllocationRequest.heading') }}
                    </div>
                </div>
                <div class="manage-allocation-request__right">
                    <drop-down-menu :label="$t('manageAllocationRequest.dropdown.label')"
                                    :value="predicate"
                                    :items="predicates"
                                    :items-counts="requestCount"
                                    :translation-prefix="'manageAllocationRequest.dropdown'"
                                    @input="requestPredicateSet($event)"/>
                </div>
            </div>
            <div class="manage-allocation-request__list">
                <table v-if="!editMode" class="manage-allocation-request__table">
                    <thead>
                        <tr>
                            <th>{{ $t('manageAllocationRequest.table.requestDate') }}</th>
                            <th>{{ $t('manageAllocationRequest.table.period') }}</th>
                            <th>{{ $t('manageAllocationRequest.table.deduction') }}</th>
                            <th>{{ $t('manageAllocationRequest.table.release') }}</th>
                            <th>{{ $t('manageAllocationRequest.table.type') }}</th>
                            <th>{{ $t('manageAllocationRequest.table.block') }}</th>
                            <th v-if="predicate === 'pending'"/>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(request, index) in requestsPaginated" :key="request.id">
                            <td>{{ request.requestDate | moment("DD/MM/YYYY") }}</td>
                            <td>{{ request.from | moment("DD/MM/YYYY") }} - {{ request.to | moment("DD/MM/YYYY") }}</td>
                            <td>
                                <span v-if="request.changes.deduction.units">
                                    {{ request.changes.deduction.units }} {{ $t('manageAllocationRequest.units') }}
                                </span>
                                <span v-if="predicate === 'modified' && request.modified.changes.deduction.units">
                                    ({{ request.modified.changes.deduction.units }} {{ $t('manageAllocationRequest.units') }})
                                </span>
                            </td>
                            <td>
                                <span v-if="request.changes.release.releaseDate">
                                    {{ request.changes.release.releaseDate | moment("DD/MM/YYYY") }}
                                </span>
                                <span v-if="predicate === 'modified' && request.modified.changes.release.releaseDate">
                                    ({{ request.modified.changes.release.releaseDate | moment("DD/MM/YYYY") }})
                                </span>
                                <span v-if="request.changes.release.releaseDays">
                                    {{ request.changes.release.releaseDays }} {{ $t('manageAllocationRequest.releaseDays') }}
                                </span>
                                <span v-if="predicate === 'modified' && request.modified.changes.release.releaseDays">
                                    ({{ request.modified.changes.release.releaseDays }} {{ $t('manageAllocationRequest.releaseDays') }})
                                </span>
                            </td>
                            <td>
                                <span v-if="request.changes.type.originalType">
                                    {{ $t('manageAllocationRequest.type.' + request.changes.type.originalType.toLowerCase()) }} >
                                </span>
                                <span v-if="request.changes.type.type">
                                    {{ $t('manageAllocationRequest.type.' + request.changes.type.type.toLowerCase()) }}
                                </span>
                            </td>
                            <td>{{ request.changes.block.requested | blockFilter }}</td>
                            <td v-if="predicate === 'pending'">
                                <div v-if="contractManager" class="manage-allocation-request__contract-manager">
                                    <button class="manage-allocation-request__btn manage-allocation-request__btn-accept"
                                            @click="requestAccept(request.id)">
                                        {{ $t('manageAllocationRequest.buttons.accept') }}
                                    </button>
                                    <button class="manage-allocation-request__btn manage-allocation-request__btn-deny"
                                            @click="requestDeny(request.id)">
                                        {{ $t('manageAllocationRequest.buttons.deny') }}
                                    </button>
                                    <button v-if="request.changes.deduction.requested || request.changes.release.requested"
                                            class="manage-allocation-request__btn manage-allocation-request__btn-edit"
                                            @click="enableEdit(request, index)">
                                        {{ $t('manageAllocationRequest.buttons.edit') }}
                                    </button>
                                </div>
                                <div v-if="!contractManager" class="manage-allocation-request__supplier">
                                    <button class="manage-allocation-request__btn manage-allocation-request__btn-deny"
                                            @click="requestCancel(request.id)">
                                        {{ $t('manageAllocationRequest.buttons.cancel') }}
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="requestsPaginated.length === 0"
                     class="manage-allocation-request__no-content">
                    <i class="fa fa-bullhorn"/>
                    <div class="manage-allocation-request__no-content-body">
                        {{ $t('manageAllocationRequest.noContent') }}
                    </div>
                </div>
                <div class="manage-allocation-request__controls">
                    <div v-if="requestsPaginated.length > 0 && !editMode"
                         class="manage-allocation-request__pagination">
                        <pagination-control :max-pages="requestsMaxPages"
                                            :current-page="page"
                                            @prev="requestPageDecrement"
                                            @next="requestPageIncrement"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/ManageAllocationRequest/ManageAllocationRequest.vue